/** Display helpers for SubscriptionPlan documents from the backend. */

export function planStatus(plan) {
  return plan?.metadata?.status || plan?.status || '—';
}

export function planBasePrice(plan) {
  return plan?.pricing?.basePrice ?? plan?.pricing?.discount?.finalPrice ?? null;
}

export function planFinalPrice(plan) {
  const stored = plan?.pricing?.discount?.finalPrice;
  if (stored != null && stored !== '') return Number(stored);
  const basePrice = plan?.pricing?.basePrice;
  if (basePrice == null) return null;
  const percent = Number(plan?.pricing?.discount?.percent) || 0;
  const base = Number(basePrice) || 0;
  return Math.round(base - (base * percent) / 100);
}

export function planCategory(plan) {
  return plan?.planCategory || plan?.planType || 'main';
}

export function planHours(plan) {
  if (plan?.hoursPerMonth != null) return plan.hoursPerMonth;
  const s = plan?.benefits?.sessionsPerMonth || 0;
  const h = plan?.benefits?.hoursPerSession || 0;
  return s * h;
}

/** Full agreement length — mirrors backend resolvePlanValidityMonths. */
export function planValidityMonths(plan) {
  const validity = Number(plan?.validityMonths);
  if (Number.isFinite(validity) && validity > 0) return Math.round(validity);
  const autoPay = Number(plan?.autoPayMonths);
  if (Number.isFinite(autoPay) && autoPay > 0) return Math.round(autoPay);
  return null;
}

/** Billing commitment sent as commitmentMonths — mirrors backend resolveCommitmentMonths fallback. */
export function planCommitmentMonths(plan) {
  return planValidityMonths(plan) ?? 3;
}

export function planFreeMonths(plan) {
  const n = Number(plan?.freeMonths);
  return Number.isFinite(n) && n >= 0 ? n : 1;
}

export function emptyPlanForm(category = 'main') {
  return {
    planName: '',
    description: '',
    planType: 'standard',
    planCategory: category,
    basePrice: '',
    discountPercent: 0,
    currency: 'INR',
    sessionsPerMonth: 4,
    hoursPerSession: 25,
    featuresText: '',
    status: 'draft',
    billingCycle: 'monthly',
    freeMonths: '',
    hoursPerMonth: '',
  };
}

export function planToForm(plan) {
  return {
    planName: plan.planName || '',
    description: plan.description || '',
    planType: plan.planType || 'standard',
    planCategory: planCategory(plan),
    basePrice: plan.pricing?.basePrice ?? '',
    discountPercent: plan.pricing?.discount?.percent ?? 0,
    currency: plan.pricing?.currency || 'INR',
    sessionsPerMonth: plan.benefits?.sessionsPerMonth ?? 4,
    hoursPerSession: plan.benefits?.hoursPerSession ?? 25,
    featuresText: (plan.benefits?.features || []).join(', '),
    status: planStatus(plan),
    billingCycle: plan.billing?.autoPay?.billingCycle || 'monthly',
    freeMonths: plan.freeMonths ?? '',
    hoursPerMonth: plan.hoursPerMonth ?? '',
  };
}

export function formToPlanPayload(form) {
  const features = String(form.featuresText || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const payload = {
    planName: form.planName.trim(),
    description: form.description?.trim() || undefined,
    planType: form.planType.trim() || 'standard',
    planCategory: form.planCategory,
    pricing: {
      basePrice: Number(form.basePrice),
      currency: form.currency || 'INR',
      discount: { percent: Number(form.discountPercent) || 0 },
    },
    billing: {
      autoPay: {
        enabled: false,
        billingCycle: form.billingCycle || 'monthly',
      },
    },
    benefits: {
      sessionsPerMonth: Number(form.sessionsPerMonth) || 1,
      hoursPerSession: Number(form.hoursPerSession) || 1,
      ...(features.length ? { features } : {}),
    },
    metadata: { status: form.status || 'draft' },
  };

  if (form.hoursPerMonth !== '') {
    payload.hoursPerMonth = Number(form.hoursPerMonth);
  }
  if (form.freeMonths !== '') {
    payload.freeMonths = Number(form.freeMonths);
  }

  return payload;
}
